import Button from "@mui/material/Button";
import Divider from "@mui/material/Divider";
import Grid from "@mui/material/Grid2";
import Popover from "@mui/material/Popover";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { Link as MuiLink } from "@mui/material";
import React, { useContext, useCallback, useMemo, ReactNode } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import LoadingInvoicePage from "../../../components/LoadingInvoicePage";
import { PageContext } from "../../../context/PageContext";
import { ChasablesContext } from "../../../context/ChasablesContext";
import { AdjustToneList } from "../../../components/AdjustToneList";
import { History } from "../../../components/History";
import { useChasable } from "../../../hooks/useChasable";
import { useHandleChase } from "../../../hooks/useHandleChase";
import { InvoiceInformation } from "./InvoiceInformation";
import { MoreInvoicesAlert } from "./MoreInvoicesAlert";
import { StatusChip } from "./StatusChip";
import { SuggestedChasableFooter } from "./SuggestedChasableFooter";
import { SuggestedChasableFilters } from "./Filters/SuggestedChasableFilters";
import { useSuggestedChasableFilters } from "./Filters/SuggestedChasableFiltersContext";
import { CustomerInsights } from "./CustomerInsights/CustomerInsights";
import { ActionTypeEnum } from "@attelas/shared";
import ChaseGroupedInvoices from "../ChaseGroupedInvoices/ChaseGroupedInvoices";
import InvoiceComments from "../../../components/InvoiceComments/InvoiceComments";
import { OrganisationFilePicker } from "../../../components/OrganisationFilePicker/OrganisationFilePicker";
import { NoReceivablesEmptyState } from "./NoReceivablesEmptyState";
import { useCustomersChasablesApi } from "../../../hooks/useCustomersChasablesApi";
import { filterHubActionsByApiFilters } from "./filterHubActionsByApiDispute";

const SuggestedChasable = () => {
  const { chasables, assignedToUserId, onAssignedToUserIdChange } = useContext(ChasablesContext);
  const filters = useSuggestedChasableFilters();
  const { chasables: apiChasables } = useCustomersChasablesApi();
  const { setNewTitle, setAction } = useContext(PageContext);
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const isFromSearch = !!searchParams.get("isFromSearch");

  const actions = useMemo(() => {
    const base =
      chasables?.actions.filter(
        ({ type }) =>
          type === ActionTypeEnum.Chasable ||
          type === ActionTypeEnum.NewInvoice ||
          type === ActionTypeEnum.PromiseToPayBroken
      ) ?? [];

    return filterHubActionsByApiFilters(base, apiChasables, {
      disputedFilter: filters.disputedFilter,
      disputedReasonFilter: filters.disputedReasonFilter,
      dueDateFilter: filters.dueDateFilter,
      currencyFilter: filters.currencyFilter,
    });
  }, [chasables?.actions, filters, apiChasables]);

  // When we have an id but current chasable is not in the filtered list: go to first action or empty page
  React.useEffect(() => {
    if (!id) return;
    const isCurrentInList =
      actions.length > 0 && actions.some((action) => action.id === id || action.externalId === id);
    if (!isCurrentInList && !isFromSearch) {
      navigate(actions.length > 0 ? `/chasables/suggested/${actions[0].id}` : "/chasables/suggested", {
        replace: true,
      });
    }
  }, [id, actions, navigate]);

  // When we're on the empty-state route and there are actions, go to the first one
  React.useEffect(() => {
    if (!id && actions.length > 0) {
      navigate(`/chasables/suggested/${actions[0].id}`, { replace: true });
    }
  }, [id, actions, navigate]);

  const { chasable, isLoading: isFullPageLoading, refetchChasable } = useChasable(id, isFromSearch);
  const isNewInvoice = actions.find((action) => action.id === chasable?.chasableId)?.type === ActionTypeEnum.NewInvoice;
  const isPromiseToPayBroken =
    actions.find((action) => action.id === chasable?.chasableId)?.type === ActionTypeEnum.PromiseToPayBroken;

  const {
    handleChase,
    handleDraftFromTemplate,
    addOtherContactsToCc,
    isLoading: isChasing,
    hasBeenChased,
    resetHasBeenChased,
  } = useHandleChase({
    invoiceIds: chasable?.invoiceDetails.id,
    customerId: chasable?.customerDetails.id,
    isNew: isNewInvoice,
    contacts: chasable?.customerDetails.contacts,
  });
  const [isLoading, setIsLoading] = React.useState(false);
  const [organisationFilePickerAnchor, setOrganisationFilePickerAnchor] = React.useState<HTMLElement | null>(null);
  const [adjustToneAnchor, setAdjustToneAnchor] = React.useState<HTMLElement | null>(null);
  const hasPrevious = actions.findIndex((action) => action.id === id || action.externalId === id) > 0;

  const hasNext = actions.findIndex((action) => action.id === id || action.externalId === id) < actions.length - 1;
  const currentIndex = actions.findIndex((action) => action.id === id || action.externalId === id) ?? -1;
  const showNavigation = currentIndex !== -1;

  React.useEffect(() => {
    setNewTitle("Receivable");
    // Cast to any to align with PageContext action ReactNode typing
    setAction((<SuggestedChasableFilters />) as unknown as ReactNode);

    return () => {
      setAction(null);
    };
  }, []);

  // No id = empty-state route: show "No receivables" and optionally "Clear filters"
  if (!id) {
    const hasFiltersApplied =
      filters.disputedFilter !== "all" ||
      filters.disputedReasonFilter.length > 0 ||
      filters.dueDateFilter !== "all" ||
      filters.currencyFilter.length > 0 ||
      assignedToUserId !== null;
    return (
      <NoReceivablesEmptyState
        hasFiltersApplied={hasFiltersApplied}
        onClearFilters={() => {
          filters.clearAllFilters();
          onAssignedToUserIdChange(null);
        }}
      />
    );
  }

  const handleNavigation = (direction: "next" | "previous") => {
    if (actions.length > 1) {
      if (currentIndex !== -1) {
        if (direction === "next" && currentIndex < actions.length - 1) {
          resetFormState();
          navigate(`/chasables/suggested/${actions[currentIndex + 1].id}`);
        } else if (direction === "previous" && currentIndex > 0) {
          resetFormState();
          navigate(`/chasables/suggested/${actions[currentIndex - 1].id}`);
        }
      }
    }
  };

  const navigateToNextChasable = () => {
    if (actions.length > 1) {
      handleNavigation("next");
    } else {
      resetFormState();
      navigate("/");
    }
  };

  const resetFormState = () => {
    resetHasBeenChased();
  };

  if (!chasable || isFullPageLoading) {
    return <LoadingInvoicePage />;
  }

  if (chasable.invoiceDetails.groupId) {
    return (
      <ChaseGroupedInvoices
        customerId={chasable.customerDetails.id}
        groupId={chasable.invoiceDetails.groupId}
        navigateToNextChasable={() => handleNavigation("next")}
        navigateToPreviousChasable={() => handleNavigation("previous")}
      />
    );
  }

  return (
    <>
      <Stack pb={16}>
        <Grid container={true} spacing={1}>
          {showNavigation && (
            <Grid container={true} spacing={1} size={12} mt={-2}>
              <Grid size={6}>
                {hasPrevious && (
                  <Button variant="text" onClick={() => handleNavigation("previous")} sx={{ marginLeft: -1 }}>
                    Previous
                  </Button>
                )}
              </Grid>
              <Grid size={6} textAlign="right">
                {hasNext && (
                  <Button variant="text" onClick={() => handleNavigation("next")} sx={{ marginRight: -1 }}>
                    Next
                  </Button>
                )}
              </Grid>
            </Grid>
          )}
          <Grid size={6}>
            <Typography variant="h2" sx={{ lineHeight: "18px" }}>
              Customer Action
            </Typography>
            <MuiLink component={Link} to={chasable.customerDetails.link ?? ""} target="_blank">
              {chasable.customerDetails.companyName ?? ""}
            </MuiLink>
          </Grid>
          <Grid size={6} sx={{ textAlign: "right" }}>
            <StatusChip status={chasable.invoiceDetails.status} dueDate={chasable.invoiceDetails.dueDate} />
          </Grid>
          {chasable.hasMoreUnpaidInvoices && (
            <Grid size={12}>
              <MoreInvoicesAlert
                companyName={chasable.customerDetails.companyName ?? ""}
                customerId={chasable.customerDetails.id}
              />
            </Grid>
          )}
          <Divider />
          <Grid size={12}>
            <InvoiceInformation
              invoice={chasable.invoiceDetails}
              companyName={chasable.customerDetails.companyName}
              accountNumber={chasable.customerDetails.accountNumber}
              assignedTo={chasable.assignedTo}
              customerLink={chasable.customerDetails.link ?? ""}
              isNewInvoice={isNewInvoice}
              isPromiseToPayBroken={isPromiseToPayBroken}
              onRefresh={refetchChasable}
            />
          </Grid>
          <Grid size={12}>
            <CustomerInsights customerDetails={chasable.customerDetails} />
          </Grid>
          <Grid size={12}>
            <InvoiceComments invoiceId={chasable.invoiceDetails.id} onCommentAdded={refetchChasable} />
          </Grid>
          <Grid size={12}>
            <History title="History" emptyText="No history" history={chasable.chasableHistory} />
          </Grid>
        </Grid>
      </Stack>
      <OrganisationFilePicker
        open={!!organisationFilePickerAnchor}
        anchorEl={organisationFilePickerAnchor}
        onClose={() => setOrganisationFilePickerAnchor(null)}
        onAdd={(files) => {
          handleChase(undefined, files);
          setOrganisationFilePickerAnchor(null);
        }}
        alreadySelectedIds={[]}
      />
      <Popover
        open={!!adjustToneAnchor}
        anchorEl={adjustToneAnchor}
        onClose={() => setAdjustToneAnchor(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <AdjustToneList
          isLoading={isLoading || isChasing}
          onSelectTone={(toneId) => {
            handleChase(toneId, []);
            setAdjustToneAnchor(null);
          }}
        />
      </Popover>
      <SuggestedChasableFooter
        chasable={chasable}
        hasBeenChased={hasBeenChased}
        isLoading={isLoading || isChasing}
        setIsLoading={setIsLoading}
        handleChaseChasable={(languageId, recipientMode) => handleChase(undefined, [], languageId, recipientMode)}
        handleDraftFromTemplate={handleDraftFromTemplate}
        navigateToNextChasable={navigateToNextChasable}
        onOpenAttachPickerForDraft={(anchorEl) => setOrganisationFilePickerAnchor(anchorEl)}
        onOpenAdjustTone={hasBeenChased ? (anchorEl) => setAdjustToneAnchor(anchorEl) : undefined}
        onAddOtherContactsToCc={hasBeenChased ? addOtherContactsToCc : undefined}
      />
    </>
  );
};

export default SuggestedChasable;
